// ./src/utils/api/authErrorHandler.ts
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

// ============================================
// Types
// ============================================

export type AuthErrorCallback = (code: number, message?: string) => void;

// ============================================
// Auth error codes that should trigger redirect
// ============================================

const AUTH_ERROR_CODES = new Set([
  OP_RESULT_CODES.UNAUTHORIZED,
  //OP_RESULT_CODES.FORBIDDEN,
  OP_RESULT_CODES.EXPIRED,
  OP_RESULT_CODES.INVALID_SIGNATURE,
  OP_RESULT_CODES.INVALID_AUDIENCE,
  OP_RESULT_CODES.INVALID_ISSUER,
  OP_RESULT_CODES.MALFORMED_TOKEN,
  OP_RESULT_CODES.NO_SIGNATURE,
]);

// ============================================
// Handler registry (singleton)
// ============================================

let authErrorHandler: AuthErrorCallback | null = null;
let isHandlingError = false;

/**
 * Register the global auth error handler.
 * Call this once at app initialization (in App.tsx).
 */
export const setAuthErrorHandler = (handler: AuthErrorCallback): void => {
  authErrorHandler = handler;
};

/**
 * Clear the auth error handler.
 * Useful for cleanup or testing.
 */
export const clearAuthErrorHandler = (): void => {
  authErrorHandler = null;
  isHandlingError = false;
};

/**
 * Check if a result code indicates an auth error.
 */
export const isAuthErrorCode = (code: number): boolean => {
  return AUTH_ERROR_CODES.has(code);
};

/**
 * Trigger the auth error handler if registered.
 * Prevents multiple simultaneous triggers.
 */
export const triggerAuthError = (code: number, message?: string): void => {
  // Prevent multiple redirects
  if (isHandlingError) {
    return;
  }

  if (authErrorHandler && isAuthErrorCode(code)) {
    isHandlingError = true;
    authErrorHandler(code, message);

    // Reset flag after a delay to allow for navigation
    setTimeout(() => {
      isHandlingError = false;
    }, 2000);
  }
};